// Buoyancy — wave-based water forces
// Engine feature: applies upward force + drag to ANY entity near water.
// No entity-type checks. Composable with other systems.
// Deterministic: fixed-point math. Wave params match ocean.ts shader.

import { FP, type Fixed } from "./fixed.js";
import { type Vec3Store, type World, type System, query } from "./ecs.js";

// Wave parameters — must match ocean.ts WGSL shader exactly
type WaveParam = { dx: Fixed; dz: Fixed; amp: Fixed; freq: Fixed; steep: Fixed; speed: Fixed };

// Only the 4 largest waves matter for physics (small detail waves are negligible for buoyancy)
const WAVES: WaveParam[] = [
  { dx: FP.from(1.0), dz: FP.from(0.0), amp: FP.from(0.8), freq: FP.from(0.15), steep: FP.from(0.5), speed: FP.from(0.4) },
  { dx: FP.from(0.7), dz: FP.from(0.7), amp: FP.from(0.5), freq: FP.from(0.25), steep: FP.from(0.45), speed: FP.from(0.3) },
  { dx: FP.from(-0.3), dz: FP.from(0.9), amp: FP.from(0.35), freq: FP.from(0.4), steep: FP.from(0.7), speed: FP.from(0.5) },
  { dx: FP.from(0.5), dz: FP.from(-0.8), amp: FP.from(0.2), freq: FP.from(0.6), steep: FP.from(0.65), speed: FP.from(0.7) },
];

function normDir(dx: Fixed, dz: Fixed): { dx: Fixed; dz: Fixed } {
  const len = FP.sqrt(FP.add(FP.mul(dx, dx), FP.mul(dz, dz)));
  if (len === 0) return { dx: FP.ZERO, dz: FP.ZERO };
  return { dx: FP.div(dx, len), dz: FP.div(dz, len) };
}

const NORM_DIRS = WAVES.map(w => normDir(w.dx, w.dz));

/** Get Gerstner wave height at a world position (deterministic, fixed-point). */
export function getWaveHeight(x: Fixed, z: Fixed, tick: number): Fixed {
  const time = ((tick * 1000 / 60) | 0) as Fixed;
  let y = FP.ZERO;
  for (let i = 0; i < WAVES.length; i++) {
    const w = WAVES[i];
    const d = NORM_DIRS[i];
    const dotDP = FP.add(FP.mul(d.dx, x), FP.mul(d.dz, z));

    // Wave group envelope — matches ocean.ts shader
    const groupFreq = FP.div(FP.mul(w.freq, FP.from(0.12)), FP.ONE);
    const groupSpeed = FP.div(w.speed, FP.from(2));
    const groupPhase = FP.add(FP.mul(dotDP, groupFreq), FP.mul(time, groupSpeed));
    const envelope = FP.add(FP.from(0.3), FP.mul(FP.from(0.7),
      FP.add(FP.from(0.5), FP.mul(FP.from(0.5), FP.sin(groupPhase)))));
    const amp = FP.mul(w.amp, envelope);

    const phase = FP.add(FP.mul(dotDP, w.freq), FP.mul(time, w.speed));
    y = FP.add(y, FP.mul(amp, FP.sin(phase)));
  }
  return y;
}

/** Get wave normal at a world position (float, for rendering). */
export function getWaveNormalFloat(x: number, z: number, timeSec: number): [number, number, number] {
  let nx = 0, ny = 1, nz = 0;
  const waveData = [
    { dx: 1.0, dz: 0.0, amp: 0.8, freq: 0.15, steep: 0.5, speed: 0.4 },
    { dx: 0.7, dz: 0.7, amp: 0.5, freq: 0.25, steep: 0.45, speed: 0.3 },
    { dx: -0.3, dz: 0.9, amp: 0.35, freq: 0.4, steep: 0.7, speed: 0.5 },
    { dx: 0.5, dz: -0.8, amp: 0.2, freq: 0.6, steep: 0.65, speed: 0.7 },
  ];
  for (const w of waveData) {
    const len = Math.sqrt(w.dx * w.dx + w.dz * w.dz);
    const ddx = w.dx / len, ddz = w.dz / len;
    const phase = (ddx * x + ddz * z) * w.freq + timeSec * w.speed;
    const wa = w.freq * w.amp;
    nx -= ddx * wa * Math.cos(phase);
    nz -= ddz * wa * Math.cos(phase);
    ny -= w.steep * wa * Math.sin(phase);
  }
  const l = Math.sqrt(nx * nx + ny * ny + nz * nz);
  return [nx / l, ny / l, nz / l];
}

export type BuoyancyConfig = {
  force: Fixed;
  drag: Fixed;
  waterLevel: Fixed;
};

export const DEFAULT_BUOYANCY: BuoyancyConfig = {
  force: FP.from(15),
  drag: FP.from(3),
  waterLevel: FP.ZERO,
};

const TICK_DT = FP.div(FP.ONE, FP.from(60));

/**
 * Creates a buoyancy system: water force + drag.
 * Operates on ALL entities with position + velocity. No entity-type checks.
 * Modifies velocity only — physics system handles position integration.
 */
export function createBuoyancySystem(
  position: Vec3Store,
  velocity: Vec3Store,
  world: World,
  config: BuoyancyConfig = DEFAULT_BUOYANCY,
): System {
  return () => {
    const { ids, count } = query(position.mask, velocity.mask);

    for (let i = 0; i < count; i++) {
      const id = ids[i];

      const waterY = FP.add(
        config.waterLevel,
        getWaveHeight(position.x[id], position.z[id], world.tick)
      );

      const submersion = FP.sub(waterY, position.y[id]);
      if (submersion <= 0) continue; // above water

      // Buoyancy: upward force proportional to submersion
      const subClamped = FP.min(submersion, FP.from(2));
      velocity.y[id] = FP.add(velocity.y[id], FP.mul(FP.mul(subClamped, config.force), TICK_DT));

      // Water drag on all axes
      const dragFactor = FP.sub(FP.ONE, FP.mul(config.drag, TICK_DT));
      velocity.x[id] = FP.mul(velocity.x[id], dragFactor);
      velocity.y[id] = FP.mul(velocity.y[id], dragFactor);
      velocity.z[id] = FP.mul(velocity.z[id], dragFactor);
    }
  };
}
